/**
 * Presentation of the shared assumption descriptors (Settings → Home buyer /
 * Investor). Labels, help and grouping are presentation only: values and the
 * available fields always come from the API descriptors. Decimal conversion is
 * exact string arithmetic; values never pass through a host float.
 */
import type { DealModelValueDescriptor } from '../../api/dealModelTypes'

export type SettingsValue = string | boolean | null
export type SettingsDisplayKind = 'money' | 'pence' | 'percent' | 'number' | 'boolean' | 'enum' | 'text' | 'date'
export interface SettingsFieldPresentation {
  label: string
  help?: string
  unit?: string
  kind: SettingsDisplayKind
}

interface FieldCopy { label: string; help?: string; unit?: string; kind?: SettingsDisplayKind }

const FIELD_COPY: Record<string, FieldCopy> = {
  homeBuyerBuyerStatus: { label: 'Buyer status', help: 'Used for estimated England and Northern Ireland stamp duty. Eligibility for first-time buyer relief is not verified.' },
  homeBuyerFundingMethod: { label: 'Buying with', help: 'Choose cash or an illustrative repayment mortgage. Funding does not change the property saving comparison.' },
  homeBuyerLtv: { label: 'Loan to value', help: 'Mortgage loan as a share of the purchase price. The remainder is your deposit.' },
  homeBuyerAnnualInterestRate: { label: 'Annual mortgage rate', help: 'Illustrative annual repayment mortgage interest rate.' },
  homeBuyerMortgageTermYears: { label: 'Mortgage term', unit: 'years', help: 'Repayment term for the illustrative monthly mortgage payment.' },
  homeBuyerMortgageFee: { label: 'Mortgage fee', help: 'One-off fee paid from cash when using a mortgage.' },
  homeBuyerConveyancingFee: { label: 'Conveyancing', help: 'Legal purchase costs common to auction and ready-home purchases.' },
  homeBuyerSurveyFee: { label: 'Survey and valuation', help: 'Survey and valuation costs common to both buying options.' },
  homeBuyerAuctionFee: { label: 'Auction buyer fees', help: 'Auction buyer premium, reservation fee and legal-pack charges. Check the actual legal pack before bidding.' },
  homeBuyerOtherBuyingCosts: { label: 'Other buying costs', help: 'Other common one-off purchase costs. Put auction-only charges in Auction buyer fees.' },
  homeBuyerTargetSavingAmount: { label: 'Minimum cash saving', help: 'Minimum cash saving required compared with buying an equivalent ready home.' },
  homeBuyerTargetSavingRatio: { label: 'Minimum saving percentage', help: 'Minimum saving as a share of the estimated total cost of buying an equivalent ready home.' },
  homeBuyerMaximumSearchBidPence: { label: 'Bid calculation ceiling', kind: 'pence', help: 'Upper bound of the bid calculation. This is not your recommended bid.' },
  homeBuyerRefurbMode: { label: 'Refurbishment method' },
  homeBuyerRefurbBudget: { label: 'Refurbishment budget' },
  additionalDwelling: { label: 'Additional property', help: 'Whether the higher-rate stamp duty surcharge applies to this purchase.' },
  legalFeesBuy: { label: 'Buying legal fees', help: 'Conveyancing and searches on the purchase.' },
  otherPurchaseCosts: { label: 'Auction and survey costs', help: 'Allowance for buyer premiums, legal-pack charges and surveys. Replace with actual fees before bidding.' },
  depositFraction: { label: 'Deposit', help: 'Deposit as a share of the purchase price.' },
  depositRatio: { label: 'Deposit', help: 'Deposit as a share of the purchase price.' },
  mortgageRate: { label: 'Annual mortgage rate', help: 'Annual interest-only mortgage rate.' },
  mortgageType: { label: 'Mortgage type' },
  bridgeLtv: { label: 'Bridge loan to value', help: 'Bridging advance as a share of the purchase price.' },
  bridgeMonthlyRate: { label: 'Monthly bridge rate', help: 'Monthly interest rate charged on the bridging advance.' },
  bridgeArrangementFraction: { label: 'Bridge arrangement fee', help: 'Arrangement fee as a share of the bridging advance.' },
  bridgeMonths: { label: 'Bridge duration', unit: 'months', help: 'Time on the bridge before refinancing.' },
  refiLtv: { label: 'Refinance loan to value', help: 'Term mortgage as a share of the value after refurbishment.' },
  refiRate: { label: 'Annual refinance rate', help: 'Annual interest-only rate on the term mortgage.' },
  refiFees: { label: 'Refinance fees', help: 'Valuation, broker and legal fees on the refinance.' },
  operatingFraction: { label: 'Operating costs', help: 'Management, maintenance and voids as a share of rent.' },
  managementFraction: { label: 'Management fee', help: 'Letting agent fees as a share of rent.' },
  maintenanceFraction: { label: 'Maintenance', help: 'Maintenance provision as a share of rent.' },
  voidsFraction: { label: 'Voids allowance', help: 'Allowance for unlet periods as a share of rent.' },
  insuranceAnnual: { label: 'Annual insurance', help: 'Landlord insurance for one year.' },
  holdMonths: { label: 'Time to sale', unit: 'months', help: 'Months from completion to sale.' },
  agentFeeFraction: { label: 'Selling agent fee', help: 'Selling agent fee as a share of gross development value.' },
  sellingLegalFees: { label: 'Selling legal fees', help: 'Conveyancing on the sale.' },
  goodMargin: { label: 'Good profit margin', help: 'A margin on gross development value at or above this level is rated Good.' },
  averageMargin: { label: 'Average profit margin', help: 'A margin on gross development value at or above this level is rated Average.' },
  killMargin: { label: 'Minimum viable margin', help: 'Below this margin the model rejects the deal.' },
  goodCashOnCash: { label: 'Good cash return', help: 'Annual cash-on-cash return at or above which the let is rated Good.' },
  averageCashOnCash: { label: 'Average cash return', help: 'Annual cash-on-cash return at or above which the let is rated Average.' },
  killCashOnCash: { label: 'Minimum viable cash return', help: 'Below this cash-on-cash return the model rejects the let.' },
  goodMoneyLeftIn: { label: 'Good capital retained', help: 'Share of project cost still tied up after refinance at or below which the project is rated Good.' },
  averageMoneyLeftIn: { label: 'Average capital retained', help: 'Share of project cost still tied up after refinance at or below which the project is rated Average.' },
  killMoneyLeftIn: { label: 'Maximum capital retained', help: 'Above this share of project cost still tied up after refinance the model rejects the project.' },
  minimumBidProfit: { label: 'Minimum project profit', help: 'Minimum profit required at the calculated maximum bid.' },
  minimumBidMargin: { label: 'Minimum bid margin', help: 'Minimum margin on gross development value required at the calculated maximum bid.' },
  minimumReadySaving: { label: 'Minimum ready-home saving', help: 'Minimum saving compared with buying an equivalent ready home.' },
  minimumReadyDiscount: { label: 'Minimum ready-home discount', help: 'Minimum discount compared with the ready-condition value.' },
  maximumSearchBidPence: { label: 'Bid calculation ceiling', kind: 'pence', help: 'Upper bound of the bid calculation. This is not your recommended bid.' },
  preLetMonths: { label: 'Time before letting', unit: 'months' },
  monthlyHoldingCost: { label: 'Monthly holding costs', help: 'Running costs before letting.' },
  lightMonths: { label: 'Light refurbishment duration', unit: 'months' },
  fullMonths: { label: 'Full refurbishment duration', unit: 'months' },
  minimumBidCashOnCash: { label: 'Minimum annual cash return', help: 'Minimum annual cash-on-cash return required at the calculated maximum bid.' },
  minimumMonthlyCashFlow: { label: 'Minimum monthly cash flow' },
  stressRateIncrease: { label: 'Interest-rate stress increase', help: 'Extra annual interest used in the mortgage stress calculation, in percentage points.' },
  roomRatePcm: { label: 'Monthly room rent' },
  occupancyFraction: { label: 'Occupancy', help: 'Share of available room-months or nights actually let.' },
  billsPcm: { label: 'Monthly bills' },
  licenceFeeAnnual: { label: 'Annual licence fee' },
  nightlyRate: { label: 'Nightly rate' },
  bookingFeeFraction: { label: 'Booking fees', help: 'Platform commission as a share of revenue.' },
  avgStayNights: { label: 'Average stay', unit: 'nights' },
  cleaningPerStay: { label: 'Cleaning per stay' },
  includeVat: { label: 'Include VAT' },
  effectiveDate: { label: 'Effective date' },
}

const ENUM_LABELS: Record<string, string> = {
  firstTime: 'First-time buyer', replacing: 'Replacing my main home', additional: 'Additional property',
  cash: 'Cash', mortgage: 'Mortgage', interest_only: 'Interest only', repayment: 'Repayment',
  auto: 'Auto', custom: 'Fixed budget', light: 'Light', standard: 'Standard', full: 'Full',
}

function readableKey(key: string): string {
  const words = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim()
  return words ? words[0]!.toUpperCase() + words.slice(1) : 'Setting'
}

export function settingsOptionLabel(value: string): string { return ENUM_LABELS[value] ?? readableKey(value) }

export function settingsFieldPresentation(descriptor: DealModelValueDescriptor): SettingsFieldPresentation {
  const copy = FIELD_COPY[descriptor.key]
  const kinds: Record<DealModelValueDescriptor['semanticType'], SettingsDisplayKind> = {
    money: 'money', moneyPence: 'pence', ratio: 'percent', percentage: 'percent', boolean: 'boolean',
    enum: 'enum', string: 'text', date: 'date', decimal: 'number', integer: 'number', count: 'number',
    months: 'number', days: 'number', areaSqm: 'number',
  }
  const units: Partial<Record<DealModelValueDescriptor['semanticType'], string>> = { months: 'months', days: 'days', areaSqm: 'm²' }
  return { label: copy?.label ?? readableKey(descriptor.key), help: copy?.help, unit: copy?.unit ?? units[descriptor.semanticType], kind: copy?.kind ?? kinds[descriptor.semanticType] }
}

export interface SettingsDescriptorGroup { id: string; title: string; description?: string; descriptors: DealModelValueDescriptor[] }
interface GroupDefinition { id: string; title: string; description?: string; keys: string[] }

const HOME_GROUPS: GroupDefinition[] = [
  { id: 'buying', title: 'Buying your home', keys: ['homeBuyerBuyerStatus', 'homeBuyerFundingMethod'] },
  { id: 'mortgage', title: 'Mortgage illustration', keys: ['homeBuyerLtv', 'homeBuyerAnnualInterestRate', 'homeBuyerMortgageTermYears', 'homeBuyerMortgageFee'] },
  { id: 'costs', title: 'Buying costs', keys: ['homeBuyerConveyancingFee', 'homeBuyerSurveyFee', 'homeBuyerAuctionFee', 'homeBuyerOtherBuyingCosts'] },
  { id: 'saving', title: 'Saving targets', description: 'The maximum sensible bid must meet both minimum savings targets.', keys: ['homeBuyerTargetSavingAmount', 'homeBuyerTargetSavingRatio'] },
  { id: 'advanced', title: 'Calculation limits', keys: ['homeBuyerMaximumSearchBidPence'] },
]

const INVESTOR_GROUPS: GroupDefinition[] = [
  { id: 'buying', title: 'Buying costs', keys: ['additionalDwelling', 'legalFeesBuy', 'otherPurchaseCosts', 'includeVat'] },
  { id: 'finance', title: 'Purchase finance', keys: ['depositFraction', 'depositRatio', 'mortgageRate', 'mortgageType', 'bridgeLtv', 'bridgeMonthlyRate', 'bridgeArrangementFraction', 'bridgeMonths'] },
  { id: 'refinance', title: 'Refinance', keys: ['refiLtv', 'refiRate', 'refiFees'] },
  { id: 'running', title: 'Running costs', keys: ['operatingFraction', 'managementFraction', 'maintenanceFraction', 'voidsFraction', 'insuranceAnnual', 'billsPcm', 'licenceFeeAnnual', 'bookingFeeFraction', 'cleaningPerStay'] },
  { id: 'holding', title: 'Holding and selling', keys: ['holdMonths', 'agentFeeFraction', 'sellingLegalFees', 'preLetMonths', 'monthlyHoldingCost', 'lightMonths', 'fullMonths'] },
  { id: 'targets', title: 'Bid targets', keys: ['minimumBidProfit', 'minimumBidMargin', 'minimumReadySaving', 'minimumReadyDiscount', 'minimumBidCashOnCash', 'minimumMonthlyCashFlow', 'stressRateIncrease'] },
  { id: 'ratings', title: 'Deal ratings', keys: ['goodMargin', 'averageMargin', 'killMargin', 'goodCashOnCash', 'averageCashOnCash', 'killCashOnCash', 'goodMoneyLeftIn', 'averageMoneyLeftIn', 'killMoneyLeftIn'] },
  { id: 'income', title: 'Income assumptions', keys: ['roomRatePcm', 'occupancyFraction', 'nightlyRate', 'avgStayNights'] },
  { id: 'advanced', title: 'Calculation limits', keys: ['maximumSearchBidPence'] },
]

/** Known fields in their readable groups; unknown authored settings stay visible under Additional assumptions. */
export function groupSettingsDescriptors(descriptors: DealModelValueDescriptor[], experience: 'homeBuyer' | 'investor'): SettingsDescriptorGroup[] {
  const definitions = experience === 'homeBuyer' ? HOME_GROUPS : INVESTOR_GROUPS
  const byKey = new Map(descriptors.map(descriptor => [descriptor.key, descriptor]))
  const groups: SettingsDescriptorGroup[] = []
  for (const group of definitions) {
    const fields = group.keys.flatMap(key => { const descriptor = byKey.get(key); if (!descriptor) return []; byKey.delete(key); return [descriptor] })
    if (fields.length) groups.push({ id: group.id, title: group.title, description: group.description, descriptors: fields })
  }
  if (byKey.size) groups.push({ id: 'additional', title: 'Additional assumptions', descriptors: [...byKey.values()] })
  return groups
}

/** Normalize only ordinary decimal notation, without passing financial values through Number. */
export function normalizeSettingsDecimal(input: string): string | null {
  const text = input.trim()
  if (!/^[+-]?(?:\d+|\d{1,3}(?:,\d{3})+)?(?:\.\d*)?$/.test(text) || !/\d/.test(text)) return null
  const sign = text.startsWith('-') ? '-' : ''
  const unsigned = text.replace(/^[+-]/, '').replaceAll(',', '')
  const [whole = '', fraction = ''] = unsigned.split('.')
  const integer = whole.replace(/^0+(?=\d)/, '') || '0'
  const decimal = fraction.replace(/0+$/, '')
  return integer === '0' && decimal === '' ? '0' : `${sign}${integer}${decimal ? `.${decimal}` : ''}`
}

/** Decimal point movement is exact for arbitrary-length values and fractions. */
export function shiftSettingsDecimal(input: string, places: number): string | null {
  const value = normalizeSettingsDecimal(input)
  if (value === null) return null
  const negative = value.startsWith('-')
  const [whole = '', fraction = ''] = value.replace(/^-/, '').split('.')
  const digits = whole + fraction
  const point = whole.length + places
  const shifted = point <= 0 ? `0.${'0'.repeat(-point)}${digits}`
    : point >= digits.length ? digits + '0'.repeat(point - digits.length)
      : `${digits.slice(0, point)}.${digits.slice(point)}`
  return normalizeSettingsDecimal(`${negative ? '-' : ''}${shifted}`)
}

export function settingsValueToDisplay(value: SettingsValue | undefined, descriptor: DealModelValueDescriptor, kind = settingsFieldPresentation(descriptor).kind): string {
  if (typeof value !== 'string') return ''
  if (kind === 'pence') return shiftSettingsDecimal(value, -2) ?? value
  if (kind === 'percent' && descriptor.semanticType === 'ratio') return shiftSettingsDecimal(value, 2) ?? value
  return value
}

export function settingsDisplayToValue(input: string, descriptor: DealModelValueDescriptor, kind = settingsFieldPresentation(descriptor).kind): string | null {
  if (input.trim() === '') return descriptor.semanticType === 'string' ? '' : null
  if (kind === 'date' || kind === 'text') return input
  const canonical = normalizeSettingsDecimal(input)
  if (canonical === null) return input
  if (kind === 'pence') return shiftSettingsDecimal(canonical, 2) ?? input
  if (kind === 'percent' && descriptor.semanticType === 'ratio') return shiftSettingsDecimal(canonical, -2) ?? input
  return canonical
}

export function formatSettingsMoney(input: string): string {
  const canonical = normalizeSettingsDecimal(input)
  if (canonical === null) return input
  const [whole = '', fraction = ''] = canonical.split('.')
  // Invalid over-precise edits remain visible and are rejected by the API validator, never rounded.
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${fraction.padEnd(2, '0')}`
}

/** One-line value for a group summary. */
export function settingsSummaryValue(descriptor: DealModelValueDescriptor, value: SettingsValue | undefined): string {
  if (value === null || value === undefined) return 'Not set'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  const presentation = settingsFieldPresentation(descriptor)
  if (presentation.kind === 'enum') return settingsOptionLabel(value)
  if (value === '') return 'Empty'
  const display = settingsValueToDisplay(value, descriptor, presentation.kind)
  if (presentation.kind === 'money' || presentation.kind === 'pence') return `£${formatSettingsMoney(display)}`
  if (presentation.kind === 'percent') return `${formatSettingsMoney(display)}%`
  return presentation.unit ? `${display} ${presentation.unit}` : display
}
